import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, X, Send, Loader2, Copy, MessageSquare, MailCheck, Reply, Paperclip, FileText, UserPlus, Check } from "lucide-react";

function sessionId() {
  let id = sessionStorage.getItem("ccfi_ai_session");
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem("ccfi_ai_session", id);
  }
  return id;
}

const QUICK_PROMPTS = [
  "What's on today?",
  "Which quotes need a follow-up?",
  "Any unpaid invoices?",
  "Remind me to order materials tomorrow 8am",
];

export const AIAssistant = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  const [draft, setDraft] = useState("");
  const [tone, setTone] = useState("professional");
  const [rewritten, setRewritten] = useState("");

  const [received, setReceived] = useState("");
  const [replyNotes, setReplyNotes] = useState("");
  const [replyTone, setReplyTone] = useState("professional");
  const [reply, setReply] = useState("");
  const [emailFile, setEmailFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const fileRef = useRef(null);

  const onEmailFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("File too large (max 4 MB)");
    const reader = new FileReader();
    const isText = /\.(txt|eml|md)$/i.test(f.name) || f.type.startsWith("text/") || f.type === "message/rfc822";
    if (isText) {
      reader.onload = () => { setReceived(String(reader.result || "").slice(0, 12000)); setEmailFile(null); toast.success(`Loaded ${f.name}`); };
      reader.readAsText(f);
    } else if (/\.(pdf|png|jpe?g|webp)$/i.test(f.name) || f.type === "application/pdf" || f.type.startsWith("image/")) {
      reader.onload = () => {
        setEmailFile({ name: f.name, mime: f.type || (f.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/png"), b64: String(reader.result).split(",")[1] });
        toast.success(`Attached ${f.name}`);
      };
      reader.readAsDataURL(f);
    } else toast.error("Use .txt, .eml, .pdf or an image of the email");
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const chat = useMutation({
    mutationFn: async (message) => (await api.post("/ai/assistant", { session_id: sessionId(), message })).data,
    onSuccess: (d) => setMessages((m) => [...m, { role: "assistant", text: d.reply }]),
    onError: (e) => setMessages((m) => [...m, { role: "assistant", text: `⚠️ ${e?.response?.data?.detail || "Something went wrong — try again."}` }]),
  });

  const rewrite = useMutation({
    mutationFn: async () => (await api.post("/ai/rewrite-email", { text: draft, tone })).data,
    onSuccess: (d) => setRewritten(d.text),
    onError: (e) => toast.error(e?.response?.data?.detail || "Rewrite failed"),
  });

  const genReply = useMutation({
    mutationFn: async () => (await api.post("/ai/analyze-email", {
      email_text: received.trim() || null,
      file_b64: emailFile?.b64 || null, file_mime: emailFile?.mime || null, file_name: emailFile?.name || null,
      tone: replyTone, instructions: replyNotes.trim() || null,
    })).data,
    onSuccess: (d) => { setAnalysis(d.analysis || null); setReply(d.reply || ""); setLead(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Analysis failed"),
  });

  const [lead, setLead] = useState(null);
  const createEnquiry = useMutation({
    mutationFn: async () => (await api.post("/ai/email-to-enquiry", {
      name: analysis?.contact?.name || "Unknown sender",
      email: analysis?.contact?.email || null,
      phone: analysis?.contact?.phone || null,
      address: analysis?.contact?.address || null,
      summary: analysis?.summary || null,
      email_text: received.trim().slice(0, 8000) || null,
    })).data,
    onSuccess: (d) => {
      setLead(d);
      toast.success(d.existing_client
        ? `${d.client_name} already exists — new deal + call-back booked`
        : `Enquiry created for ${d.client_name} — call-back on the scheduler`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create enquiry"),
  });

  const send = (text) => {
    const msg = (text || input).trim();
    if (!msg || chat.isPending) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    chat.mutate(msg);
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-assistant-fab"
        className="fixed bottom-24 right-4 z-50 w-12 h-12 rounded-full bg-gradient-to-br from-cyan-600 to-blue-700 text-white shadow-lg shadow-cyan-900/30 hover:scale-105 transition-transform flex items-center justify-center"
        title="CCFI Assistant"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </button>

      {open && (
        <div className="fixed bottom-[9.5rem] right-4 z-50 w-[min(400px,calc(100vw-2rem))] h-[min(540px,calc(100vh-12rem))] bg-white border border-slate-200 rounded-sm shadow-2xl flex flex-col overflow-hidden" data-testid="ai-assistant-panel">
          <div className="bg-[#0b1526] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-sm flex items-center gap-1.5"><Sparkles size={14} className="text-cyan-400" /> CCFI Assistant</div>
              <div className="text-[10px] text-slate-400">Live CRM data · tasks · emails</div>
            </div>
            <div className="flex rounded-sm overflow-hidden border border-slate-600">
              <button onClick={() => setTab("chat")} data-testid="ai-tab-chat" className={`px-2.5 py-1 text-[11px] font-semibold flex items-center gap-1 ${tab === "chat" ? "bg-cyan-600 text-white" : "text-slate-300"}`}><MessageSquare size={11} /> Chat</button>
              <button onClick={() => setTab("rewrite")} data-testid="ai-tab-rewrite" className={`px-2.5 py-1 text-[11px] font-semibold flex items-center gap-1 ${tab === "rewrite" ? "bg-cyan-600 text-white" : "text-slate-300"}`}><MailCheck size={11} /> Rewrite</button>
              <button onClick={() => setTab("reply")} data-testid="ai-tab-reply" className={`px-2.5 py-1 text-[11px] font-semibold flex items-center gap-1 ${tab === "reply" ? "bg-cyan-600 text-white" : "text-slate-300"}`}><Reply size={11} /> Reply</button>
            </div>
          </div>

          {tab === "chat" ? (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-slate-50" data-testid="ai-messages">
                {messages.length === 0 && (
                  <div className="pt-4">
                    <p className="text-xs text-slate-500 text-center mb-3">Ask about your schedule, quotes, invoices — or tell me to set a reminder.</p>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {QUICK_PROMPTS.map((p) => (
                        <button key={p} onClick={() => send(p)} data-testid="ai-quick-prompt" className="text-[11px] px-2.5 py-1.5 rounded-full border border-cyan-200 bg-white text-cyan-700 hover:bg-cyan-50 transition-colors">
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-sm px-3 py-2 text-[13px] whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-cyan-600 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                      {m.text}
                      {m.role === "assistant" && (
                        <button onClick={() => { navigator.clipboard.writeText(m.text); toast.success("Copied"); }} className="block mt-1 text-[10px] text-slate-400 hover:text-cyan-600"><Copy size={10} className="inline mr-0.5" /> copy</button>
                      )}
                    </div>
                  </div>
                ))}
                {chat.isPending && <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 size={13} className="animate-spin" /> thinking…</div>}
              </div>
              <div className="p-2.5 border-t border-slate-200 flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask or say 'remind me to…'" className="rounded-sm h-10 text-sm" data-testid="ai-chat-input" />
                <Button onClick={() => send()} disabled={chat.isPending || !input.trim()} className="rounded-sm h-10 w-10 p-0 bg-cyan-600 hover:bg-cyan-700 text-white shrink-0" data-testid="ai-chat-send">
                  <Send size={15} />
                </Button>
              </div>
            </>
          ) : tab === "reply" ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
              <p className="text-[11px] text-slate-500">Paste or upload an email — I'll analyse it and pick the best CCFI reply template.</p>
              <Textarea value={received} onChange={(e) => setReceived(e.target.value)} placeholder="Paste the received email here (include the sender's name if you can)…" className="rounded-sm min-h-[100px] text-sm" data-testid="ai-reply-input" />
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept=".txt,.eml,.md,.pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf,text/plain,message/rfc822" onChange={onEmailFile} className="hidden" data-testid="ai-reply-file-input" />
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} className="rounded-sm h-8 text-xs" data-testid="ai-reply-upload-btn">
                  <Paperclip size={12} className="mr-1.5" /> Upload email (.eml, .txt, .pdf, screenshot)
                </Button>
              </div>
              {emailFile && (
                <div className="flex items-center justify-between bg-cyan-50 border border-cyan-200 rounded-sm px-2.5 py-1.5 text-[11px] text-cyan-800" data-testid="ai-reply-file-chip">
                  <span className="flex items-center gap-1.5 truncate"><FileText size={12} /> {emailFile.name}</span>
                  <button onClick={() => setEmailFile(null)} className="text-cyan-500 hover:text-rose-600 ml-2" data-testid="ai-reply-file-remove"><X size={12} /></button>
                </div>
              )}
              <Input value={replyNotes} onChange={(e) => setReplyNotes(e.target.value)} placeholder="Optional: what to say — e.g. we can do Thursday, price is firm…" className="rounded-sm h-9 text-sm" data-testid="ai-reply-notes" />
              <div className="flex flex-wrap gap-1.5">
                {["professional", "friendly", "firm but polite", "short & direct"].map((t) => (
                  <button key={t} onClick={() => setReplyTone(t)} data-testid={`ai-reply-tone-${t.split(" ")[0]}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${replyTone === t ? "bg-cyan-600 text-white border-cyan-600" : "border-slate-300 text-slate-600 hover:border-cyan-400"}`}>
                    {t}
                  </button>
                ))}
              </div>
              <Button onClick={() => genReply.mutate()} disabled={genReply.isPending || (!received.trim() && !emailFile)} className="rounded-sm w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="ai-reply-btn">
                {genReply.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Reply size={14} className="mr-1.5" />} Analyse & suggest reply
              </Button>
              {analysis && (
                <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-1.5" data-testid="ai-reply-analysis">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800" data-testid="ai-reply-category">{(analysis.category || "other").replace(/_/g, " ")}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${analysis.urgency === "high" ? "bg-rose-100 text-rose-700" : analysis.urgency === "low" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`} data-testid="ai-reply-urgency">{analysis.urgency} urgency</span>
                  </div>
                  <p className="text-[12px] text-slate-700">{analysis.summary}</p>
                  {analysis.key_points?.length > 0 && (
                    <ul className="text-[11px] text-slate-500 list-disc pl-4 space-y-0.5">
                      {analysis.key_points.map((k, i) => <li key={i}>{k}</li>)}
                    </ul>
                  )}
                  <div className="text-[11px] text-slate-500 pt-0.5">Best template: <span className="font-semibold text-slate-700" data-testid="ai-reply-template-name">{analysis.template_name}</span></div>
                  {analysis.contact?.name && (
                    <div className="text-[11px] text-slate-500" data-testid="ai-reply-contact">
                      From: <span className="font-semibold text-slate-700">{analysis.contact.name}</span>
                      {analysis.contact.phone ? ` · ${analysis.contact.phone}` : ""}{analysis.contact.email ? ` · ${analysis.contact.email}` : ""}
                    </div>
                  )}
                  {analysis.contact?.name && !lead && (
                    <Button size="sm" onClick={() => createEnquiry.mutate()} disabled={createEnquiry.isPending}
                      className="rounded-sm w-full h-8 mt-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ai-create-enquiry-btn">
                      {createEnquiry.isPending ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <UserPlus size={12} className="mr-1.5" />}
                      Create enquiry + call-back
                    </Button>
                  )}
                  {lead && (
                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1.5 mt-1" data-testid="ai-lead-created">
                      <Check size={12} /> {lead.existing_client ? "Existing client — deal + call-back added" : "Client, deal & call-back created"} ·
                      <a href={`/clients/${lead.client_id}`} className="underline font-semibold">open client</a>
                    </div>
                  )}
                </div>
              )}
              {reply && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-[13px] whitespace-pre-wrap text-slate-800" data-testid="ai-reply-output">
                  {reply}
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(reply); toast.success("Reply copied — paste into your email"); }} className="rounded-sm mt-2 h-8 w-full" data-testid="ai-reply-copy">
                    <Copy size={12} className="mr-1.5" /> Copy reply
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
              <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Paste the email or message you want rewritten…" className="rounded-sm min-h-[130px] text-sm" data-testid="ai-rewrite-input" />
              <div className="flex flex-wrap gap-1.5">
                {["professional", "friendly", "firm but polite", "short & direct"].map((t) => (
                  <button key={t} onClick={() => setTone(t)} data-testid={`ai-tone-${t.split(" ")[0]}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${tone === t ? "bg-cyan-600 text-white border-cyan-600" : "border-slate-300 text-slate-600 hover:border-cyan-400"}`}>
                    {t}
                  </button>
                ))}
              </div>
              <Button onClick={() => rewrite.mutate()} disabled={rewrite.isPending || !draft.trim()} className="rounded-sm w-full bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="ai-rewrite-btn">
                {rewrite.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} Rewrite
              </Button>
              {rewritten && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-[13px] whitespace-pre-wrap text-slate-800" data-testid="ai-rewrite-output">
                  {rewritten}
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(rewritten); toast.success("Copied — paste it anywhere"); }} className="rounded-sm mt-2 h-8 w-full" data-testid="ai-rewrite-copy">
                    <Copy size={12} className="mr-1.5" /> Copy rewritten email
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};
